import React, { useEffect, useMemo, useState, Fragment } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_ROOT } from '../../service/Services';
import { Document, Page, PDFViewer, StyleSheet, Text, View, Image } from '@react-pdf/renderer';
import Services from '../../service/Services';

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 11,
    paddingTop: 30,
    paddingLeft: 40,
    paddingRight: 40,
    lineHeight: 1.5,
    flexDirection: 'column',
  },
  header: {
    marginBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    borderBottomStyle: 'solid',
    paddingBottom: 8,
  },
  companyRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  companyLeft: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  logo: { width: 60, height: 60, objectFit: 'contain' },
  companyText: { marginLeft: 10 },
  companyName: { fontSize: 14, fontWeight: 700 },
  companySub: { fontSize: 10, color: '#4b5563' },
  title: { fontSize: 16, fontWeight: 700 },
  subTitle: { fontSize: 10, color: '#4b5563' },
  section: { marginTop: 10 },
  row: { flexDirection: 'row', justifyContent: 'space-between' },
  label: { fontSize: 10, color: '#6b7280' },
  value: { fontSize: 11, color: '#111827' },
  table: { marginTop: 10 },
  th: { fontSize: 10, fontWeight: 700, paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#e5e7eb' },
  tdRow: { flexDirection: 'row', paddingVertical: 6, borderBottomWidth: 1, borderBottomColor: '#f3f4f6' },
  td: { fontSize: 10, flexGrow: 1 },
  tdQty: { width: 60, textAlign: 'right' },
  tdRate: { width: 70, textAlign: 'right' },
  tdAmt: { width: 80, textAlign: 'right' },
  total: { marginTop: 8, textAlign: 'right', fontSize: 12, fontWeight: 700 },
});

function DeliveryChallanDocument({ note, company, logoUrl }) {
  const items = Array.isArray(note?.items) ? note.items : [];
  const total = items.reduce((sum, it) => sum + ((it?.rate || 0) * (it?.quantity || 0)), 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.header}>
          <View style={styles.companyRow}>
            <View style={styles.companyLeft}>
              {logoUrl ? <Image style={styles.logo} src={logoUrl} /> : null}
              <View style={styles.companyText}>
                <Text style={styles.companyName}>{company?.name || 'Zaptas Technologies Pvt. Ltd.'}</Text>
                {company?.address ? <Text style={styles.companySub}>{company.address}</Text> : null}
                {company?.gstno ? <Text style={styles.companySub}>GST: {company.gstno}</Text> : null}
                {company?.web ? <Text style={styles.companySub}>{company.web}</Text> : null}
              </View>
            </View>
            <View>
              <Text style={styles.title}>Delivery Challan</Text>
              <Text style={styles.subTitle}>No: {note?.noteNumber || 'N/A'}</Text>
              <Text style={styles.subTitle}>Date: {note?.createdAt ? new Date(note.createdAt).toLocaleDateString() : 'N/A'}</Text>
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <View>
              <Text style={styles.label}>Bill To</Text>
              <Text style={styles.value}>{note?.customerId?.name || 'N/A'}</Text>
              {note?.customerId?.address ? <Text style={styles.subTitle}>{note.customerId.address}</Text> : null}
              {note?.customerId?.gstno ? <Text style={styles.subTitle}>GST: {note.customerId.gstno}</Text> : null}
            </View>
            <View>
              <Text style={styles.label}>Delivery Details</Text>
              <Text style={styles.value}>Delivery Date: {note?.deliveryDate ? new Date(note.deliveryDate).toLocaleDateString() : 'N/A'}</Text>
              <Text style={styles.subTitle}>Delivered By: {note?.deliveredBy || 'N/A'}</Text>
              <Text style={styles.subTitle}>Status: {note?.status || 'N/A'}</Text>
            </View>
            <View>
              <Text style={styles.label}>Sales Person</Text>
              <Text style={styles.value}>{note?.salesPersonId?.name || 'N/A'}</Text>
              {note?.remarks ? <Text style={styles.subTitle}>Remarks: {note.remarks}</Text> : null}
            </View>
          </View>
        </View>

        <View style={styles.section}>
          <View style={styles.row}>
            <View>
              <Text style={styles.label}>Delivered By</Text>
              <Text style={styles.value}>{note?.deliveredBy || 'N/A'}</Text>
            </View>
            <View>
              <Text style={styles.label}>Status</Text>
              <Text style={styles.value}>{note?.status || 'N/A'}</Text>
            </View>
            <View>
              <Text style={styles.label}>Remarks</Text>
              <Text style={styles.value}>{note?.remarks || '—'}</Text>
            </View>
          </View>
        </View>

        <View style={[styles.section, styles.table]}>
          <View style={[styles.tdRow, { borderBottomColor: '#e5e7eb' }]}>
            <Text style={[styles.td, styles.th]}>Item</Text>
            <Text style={[styles.tdQty, styles.th]}>Qty</Text>
            <Text style={[styles.tdRate, styles.th]}>Rate</Text>
            <Text style={[styles.tdAmt, styles.th]}>Amount</Text>
          </View>
          {items.map((it, idx) => (
            <View key={idx} style={styles.tdRow}>
              <Text style={styles.td}>{it?.itemId?.itemName || 'Item'}{it?.itemId?.itemCode ? ` (${it.itemId.itemCode})` : ''}</Text>
              <Text style={styles.tdQty}>{it?.quantity || 0}</Text>
              <Text style={styles.tdRate}>{Number(it?.rate || 0).toFixed(2)}</Text>
              <Text style={styles.tdAmt}>{Number((it?.rate || 0) * (it?.quantity || 0)).toFixed(2)}</Text>
            </View>
          ))}
          <Text style={styles.total}>Total: {Number(total).toFixed(2)}</Text>
        </View>

        <View style={[styles.section, { marginTop: 18 }]}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View>
              <Text style={styles.label}>Prepared By</Text>
              <Text style={styles.subTitle}>__________________________</Text>
            </View>
            <View>
              <Text style={styles.label}>Checked By</Text>
              <Text style={styles.subTitle}>__________________________</Text>
            </View>
            <View>
              <Text style={styles.label}>Received By</Text>
              <Text style={styles.subTitle}>__________________________</Text>
            </View>
          </View>
          <View style={{ marginTop: 10 }}>
            <Text style={styles.subTitle}>For and on behalf of {company?.name || 'the company'}</Text>
            <Text style={styles.subTitle}>This is a system generated document.</Text>
          </View>
        </View>
      </Page>
    </Document>
  );
}

export default function DeliveryChallanPrint() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);
  const [company, setCompany] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${API_ROOT}/delivery-note/${id}`);
        if (mounted) {
          if (res?.data?.success) {
            setNote(res.data.data);
            setError(null);
          } else {
            setError('Delivery note not found');
          }
        }
      } catch (e) {
        if (mounted) setError('Error fetching delivery note');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [id]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await Services.Common.company();
        if (!mounted) return;
        const cmp = Array.isArray(res?.data) ? res.data[0] : (Array.isArray(res) ? res[0] : res);
        setCompany(cmp || null);
        const rawLogo = cmp?.logo;
        const resolvedLogo = rawLogo ? (rawLogo.startsWith('http') ? rawLogo : rawLogo) : null;
        setLogoUrl(resolvedLogo);
      } catch (e) {
        setCompany(null);
        setLogoUrl(null);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const doc = useMemo(() => note ? <DeliveryChallanDocument note={note} company={company} logoUrl={logoUrl} /> : null, [note, company, logoUrl]);

  const handleDownload = async () => {
    if (!note) return;
    const { pdf } = await import('@react-pdf/renderer');
    const instance = pdf(<DeliveryChallanDocument note={note} company={company} logoUrl={logoUrl} />);
    const blob = await instance.toBlob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Delivery_Challan_${note?.noteNumber || id}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('download') && note) {
      handleDownload();
    }
  }, [location.search, note]);

  const handleClose = () => {
    if (window.history.state && window.history.state.idx > 0) {
      navigate(-1);
    } else {
      navigate('/invoices'); // fallback page if no history
    }
  };

  return (
    <Fragment>
      <div className="min-h-screen bg-gray-50 py-4">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Delivery Challan Preview</h1>
              {note?.noteNumber ? (
                <p className="text-sm text-gray-500">#{note.noteNumber}</p>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleClose}
                className="px-3 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handleDownload}
                disabled={!note}
                className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                Download PDF
              </button>
            </div>
          </div>

          {loading ? (
            <div className="bg-white border border-gray-200 rounded-md p-6 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-3 text-gray-600">Loading...</p>
            </div>
          ) : error ? (
            <div className="bg-white border border-red-200 rounded-md p-6 text-center">
              <p className="text-red-600">{error}</p>
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
              {doc ? (
                <PDFViewer width="100%" height={650} showToolbar={true}>
                  {doc}
                </PDFViewer>
              ) : null}
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
}
